import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import { useAuth } from "@auth/AuthContext";

/**
 * LWI-582: Fixed top banner shown only during an active impersonation session.
 * Renders the admin (impersonator) and target identities, a live countdown to
 * the session's TTL, and an Exit button. When the countdown reaches zero we
 * call exitImpersonation() — the server has already revoked the token at that
 * point, so this is mostly client-side cleanup.
 */
export default function ImpersonationBanner() {
  const { mode, impersonation, exitImpersonation } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(() =>
    computeSecondsLeft(impersonation?.expires_at)
  );

  const expiresAtIso = impersonation?.expires_at;

  useEffect(() => {
    if (mode !== "impersonation" || !expiresAtIso) return undefined;
    setSecondsLeft(computeSecondsLeft(expiresAtIso));
    const interval = setInterval(() => {
      setSecondsLeft(computeSecondsLeft(expiresAtIso));
    }, 1000);
    return () => clearInterval(interval);
  }, [mode, expiresAtIso]);

  // When the countdown hits zero, attempt a graceful exit. The server has
  // typically already revoked the token at this point — exitImpersonation()
  // is best-effort and idempotent. The scoped 401 handler in AuthContext is
  // the safety net for the race where the server-side cleanup job revokes
  // first and a concurrent request 401s.
  //
  // LWI-602: compute fresh from expiresAtIso rather than gating on the
  // secondsLeft state. On the first admin→impersonation transition the
  // banner has already been mounted (in admin mode) with secondsLeft = 0,
  // and the tick effect's setSecondsLeft hasn't propagated to this render
  // yet — reading the stale 0 used to fire exit immediately on entering
  // emulation. secondsLeft stays in deps so this still re-fires every tick.
  useEffect(() => {
    if (mode !== "impersonation" || !expiresAtIso) return;
    if (computeSecondsLeft(expiresAtIso) > 0) return;
    exitImpersonation?.();
  }, [mode, secondsLeft, expiresAtIso, exitImpersonation]);

  const countdownLabel = useMemo(
    () => formatCountdown(secondsLeft),
    [secondsLeft]
  );

  if (mode !== "impersonation" || !impersonation) {
    return null;
  }

  const targetName = impersonation.target?.name || "user";
  const targetEmail = impersonation.target?.email || "";
  const adminName = impersonation.admin?.name || "admin";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        width: "100%",
      }}
      data-testid="impersonation-banner"
    >
      <Alert
        severity="warning"
        variant="filled"
        icon={false}
        sx={{
          borderRadius: 0,
          alignItems: "center",
          "& .MuiAlert-message": { flex: 1 },
        }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => exitImpersonation?.()}
            sx={{ borderColor: "currentColor" }}
            data-testid="impersonation-banner-exit"
          >
            Exit emulation
          </Button>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <strong>
            You ({adminName}) are emulating {targetName}
          </strong>
          {targetEmail && (
            <span style={{ opacity: 0.85 }}>({targetEmail})</span>
          )}
          <Box
            component="span"
            sx={{ ml: "auto", fontVariantNumeric: "tabular-nums" }}
          >
            expires in {countdownLabel}
          </Box>
        </Box>
      </Alert>
    </Box>
  );
}

function computeSecondsLeft(expiresAtIso) {
  if (!expiresAtIso) return 0;
  const diffMs = new Date(expiresAtIso).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return 0;
  return Math.max(0, Math.floor(diffMs / 1000));
}

function formatCountdown(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
